/* CoverReveal — one-shot cinematic reveal for interactive gallery covers.
   Composes ON TOP of any outer scroll-driven parallax: the parent retains
   its scroll-coupled transform on a wrapper element; CoverReveal only
   animates the IMMEDIATE inner child (the image surface) and the title /
   subtitle. No transform competition.

   Sequence:
     0.00s  inner image: scale 1.05
     1.20s  inner image: scale 1.00   (cubic-bezier easing)
     0.35s  title fade-up
     0.55s  subtitle fade-up
     1.00s  gold hairline draws between title + subtitle (scaleX 0→1, 600ms)

   One-shot per mount. Reduced motion: static, but the hairline still renders
   instantly (brand cue).
*/
import { motion } from "framer-motion";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const EASE = [0.22, 1, 0.36, 1];
const GOLD = "#C9A55C";

export default function CoverReveal({
  active = true,
  title,
  subtitle,
  children,
  "data-testid": testid = "cover-reveal",
}) {
  const rm = prefersReducedMotion();
  // active=false → render children as-is, no animation, no title/subtitle injection.
  // This is the path for standard (non-interactive) galleries.
  if (!active) return <>{children}</>;

  return (
    <div data-testid={testid} className="relative w-full h-full">
      <motion.div
        className="absolute inset-0 will-change-transform"
        initial={rm ? { scale: 1 } : { scale: 1.05 }}
        animate={{ scale: 1 }}
        transition={{ duration: rm ? 0 : 1.2, ease: EASE }}
      >
        {children}
      </motion.div>

      {(title || subtitle) && (
        <div className="absolute inset-x-0 bottom-0 p-8 lg:p-12 text-center text-[#F5EFE0] z-10 pointer-events-none">
          {title && (
            <motion.h1
              initial={rm ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: rm ? 0 : 0.7, delay: rm ? 0 : 0.35, ease: EASE }}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05]"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {title}
            </motion.h1>
          )}

          {/* Gold hairline — couture finish between title and subtitle */}
          <motion.span
            aria-hidden="true"
            initial={rm ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{
              scaleX: { duration: rm ? 0 : 0.6, delay: rm ? 0 : 1.0, ease: EASE },
              opacity: { duration: rm ? 0 : 0.2, delay: rm ? 0 : 1.0 },
            }}
            className="inline-block w-[100px] h-px my-3 origin-left"
            style={{ backgroundColor: GOLD }}
          />

          {subtitle && (
            <motion.p
              initial={rm ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              animate={{ opacity: 0.8, y: 0 }}
              transition={{ duration: rm ? 0 : 0.7, delay: rm ? 0 : 0.55, ease: EASE }}
              className="mt-1 text-[11px] uppercase tracking-[0.32em]"
            >
              {subtitle}
            </motion.p>
          )}
        </div>
      )}
    </div>
  );
}
